// Per-student band pricing — the founder-approved model (docs/product/PRICING_TIERS_ROI.md §B).
//
// Classes 1–5 ₹20 · 6–10 ₹30 · 11–12 ₹40 per student/month; annual prepay = 20% off (₹16/24/32
// effective); a ₹3,999/mo minimum-billing floor for small schools; Starter = a flat ₹3,999/mo SKU;
// Premium = the Growth band + one flat ₹7,999/mo AI & Automation pack per school.
//
// SINGLE SOURCE OF TRUTH: the numbers are platform-governed (backend core/pricing_config.py,
// console at /platform/pricing) and served publicly as the `pricing_model` field of
// GET /api/public/marketing — build the live model with `fromConfig(pricingModel)`. The hardcoded
// constants below are a non-evidence computational fallback only. Public evidence surfaces verify
// the backend provenance and complete fields before calling `fromConfig`, so these values are never
// presented as the current proposal after a failed or partial response.
//
// ALL money in this module is INTEGER PAISE (the repo money rail) — render only via inr()/inrFull()
// from lib/utils at the display edge, never raw paise. This is the marketing ESTIMATOR math; the
// backend billing mirror (and the future charge-path seam) is core/billing.py `compute_banded_gross`
// (which reads the same config) — keep the fallback constants in sync with its code defaults.

export const BANDS = [
  { id: "b1_5", label: "Classes 1–5", ratePaise: 2000 },
  { id: "b6_10", label: "Classes 6–10", ratePaise: 3000 },
  { id: "b11_12", label: "Classes 11–12", ratePaise: 4000 },
];

export const ANNUAL_DISCOUNT_BPS = 2000; // 20% off on annual prepay (basis points)
export const FLOOR_MONTHLY_PAISE = 399900; // ₹3,999/mo minimum billing floor (small schools)
export const STARTER_FLAT_MONTHLY_PAISE = 399900; // Starter = a flat per-school SKU at the floor
export const STARTER_STUDENT_CAP = 300; // Starter flat SKU student ceiling (strategy §7)
export const AI_PACK_MONTHLY_PAISE = 799900; // Premium = band + this flat AI & Automation pack /mo

// MODEL 2 — "Fixed plans" (founder two-model decision, 2026-07-13): a school may instead pay one
// flat monthly price per plan tier; features come from the SAME plan-tier matrix in both models.
// Enterprise is intentionally absent (= custom quote).
// SINGLE PRICE AUTHORITY (2026-08-14): the LIVE fixed prices are derived server-side from the
// billing catalogue (plans.price) and every display surface reads them ONLY from the served
// payload (`pricing_integrity.rows` → buildFixedCards; "Verify with us" when absent — never these
// constants). This list is a computational-shape fallback for `fromConfig(null)` alone; keep it
// mirroring core/pricing_config DEFAULT_PRICING_MODEL.fixed_plans (the backend's own no-record
// fallback), which mirrors the seeded catalogue.
export const FIXED_PLANS = [
  { key: "starter", monthlyPaise: 499900 },
  { key: "growth", monthlyPaise: 999900 },
  { key: "premium", monthlyPaise: 1999900 },
];

// A user-typed count → a safe non-negative integer (garbage/negatives/blank → 0).
function toCount(v) {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// A served value → integer paise ≥ 0, else the fallback (a malformed config field never breaks UI).
function toPaise(v, fallback) {
  const n = Math.round(Number(v));
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

/**
 * The served platform pricing config → the estimator model. `pricingModel` is the `pricing_model`
 * payload of GET /api/public/marketing (bands carry `key`/`label`/`monthly_paise`); null/partial/
 * garbage input falls back FIELD-BY-FIELD to the constants above, so the estimator always renders —
 * offline, mid-fetch, or off a mangled value alike. Returns
 * { bands:[{id,label,ratePaise}], annualDiscountBps, floorMonthlyPaise, starterFlatMonthlyPaise,
 *   starterStudentCap, aiPackMonthlyPaise, fixedPlans:[{key,monthlyPaise}] } — all money integer paise.
 */
export function fromConfig(pricingModel) {
  const pm = pricingModel && typeof pricingModel === "object" ? pricingModel : {};
  let bands = null;
  if (Array.isArray(pm.bands) && pm.bands.length) {
    bands = pm.bands
      .map((b, i) => ({
        id: String((b && (b.key || b.id)) || `band_${i}`),
        // data labels use plain hyphens ("Classes 1-5") — typeset with an en dash for display
        label: String((b && (b.label || b.key)) || `Band ${i + 1}`).replace(/-/g, "–"),
        ratePaise: toPaise(b && (b.monthly_paise ?? b.ratePaise), -1),
      }))
      .filter((b) => b.ratePaise >= 0);
    if (!bands.length) bands = null;
  }
  // fixed_plans (Model 2): served [{key, monthly_paise}] → [{key, monthlyPaise}], key-whitelisted;
  // any malformed/missing tier falls back to its constant so the fixed panel always renders complete.
  const servedFixed = Array.isArray(pm.fixed_plans) ? pm.fixed_plans : [];
  const fixedPlans = FIXED_PLANS.map((fp) => {
    const hit = servedFixed.find((p) => p && (p.key === fp.key));
    return { key: fp.key, monthlyPaise: toPaise(hit && (hit.monthly_paise ?? hit.monthlyPaise), fp.monthlyPaise) };
  });
  const bps = Math.round(Number(pm.annual_discount_bps));
  const cap = Math.round(Number(pm.starter_student_cap));
  return {
    bands: bands || BANDS.map((b) => ({ ...b })),
    annualDiscountBps: Number.isFinite(bps) && bps >= 0 && bps < 10000 ? bps : ANNUAL_DISCOUNT_BPS,
    floorMonthlyPaise: toPaise(pm.floor_monthly_paise, FLOOR_MONTHLY_PAISE),
    starterFlatMonthlyPaise: toPaise(pm.starter_flat_monthly_paise, STARTER_FLAT_MONTHLY_PAISE),
    starterStudentCap: Number.isFinite(cap) && cap >= 0 ? cap : STARTER_STUDENT_CAP,
    aiPackMonthlyPaise: toPaise(pm.ai_pack_monthly_paise, AI_PACK_MONTHLY_PAISE),
    fixedPlans,
  };
}

// 12 months at (10000 − discount) bps. Exact integer paise for every band multiple (rates are
// multiples of ₹5); round-half-up otherwise so the result is ALWAYS integer paise.
export function annualFromMonthlyPaise(monthlyPaise, discountBps = ANNUAL_DISCOUNT_BPS) {
  return Math.floor(((monthlyPaise * 12 * (10000 - discountBps)) + 5000) / 10000);
}

// The per-student rate a band costs on annual billing (₹16/24/32 in paise) — for the band table.
export function annualEffectiveRatePaise(ratePaise, discountBps = ANNUAL_DISCOUNT_BPS) {
  return Math.floor(((ratePaise * (10000 - discountBps)) + 5000) / 10000);
}

/**
 * Pure per-student estimate. `counts` maps band id → student count ({b1_5, b6_10, b11_12});
 * `cycle` is "monthly" | "yearly"; `aiPack` adds the flat Premium pack (per school, same cycle);
 * `model` is a `fromConfig()` result — pass the served config to price with the live platform
 * numbers (omitted = the fallback constants, unchanged behaviour).
 * Returns integer-paise fields only: perBand[], rawMonthlyPaise, floorApplied, monthlyPaise (the
 * floored monthly bill incl. pack), annualPaise (12 mo at 20% off), totalPaise (the requested
 * cycle's charge) and effectiveMonthlyPaise. Zero students bill zero — the floor is a minimum for
 * a billed school, never a charge for nobody.
 */
export function estimateBanded(counts, cycle = "monthly", { aiPack = false, model = null } = {}) {
  const m = model || fromConfig(null);
  const yearly = cycle === "yearly";
  const perBand = m.bands.map((b) => {
    const students = toCount(counts && counts[b.id]);
    return { ...b, students, monthlyPaise: students * b.ratePaise };
  });
  const students = perBand.reduce((s, b) => s + b.students, 0);
  const rawMonthlyPaise = perBand.reduce((s, b) => s + b.monthlyPaise, 0);
  const floorApplied = students > 0 && rawMonthlyPaise < m.floorMonthlyPaise;
  const packPaise = aiPack && students > 0 ? m.aiPackMonthlyPaise : 0;
  const monthlyPaise = (floorApplied ? m.floorMonthlyPaise : rawMonthlyPaise) + packPaise;
  const annualPaise = annualFromMonthlyPaise(monthlyPaise, m.annualDiscountBps);
  return {
    cycle: yearly ? "yearly" : "monthly",
    students,
    perBand,
    rawMonthlyPaise,
    floorApplied,
    aiPackPaise: packPaise,
    monthlyPaise,
    annualPaise,
    totalPaise: yearly ? annualPaise : monthlyPaise,
    effectiveMonthlyPaise: yearly ? Math.floor((annualPaise + 6) / 12) : monthlyPaise,
  };
}
